import { useState, useEffect, useRef, useCallback } from 'react';
import { logError } from '../../components/LogError';

interface UseInfiniteScrollProps<T> {
  data: T[];
  numItemsPerPage: number;
  fetchMoreRef?: number;
  callback?: () => Promise<T[]>;
  totalRows?: number;
}

const useServerSideScroll = <T,>({ data, totalRows, callback }: UseInfiniteScrollProps<T>) => {
  const [results, setResults] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasNextPage, setHasNextPage] = useState(true);

  const process = async (prevResults?: T[]) => {
    setIsLoading(true);

    try {
      if ((prevResults || []).length >= (totalRows || 0)) {
        setHasNextPage(false);
        return;
      }

      const dataSet = await callback?.();
      if (!dataSet?.length) {
        setHasNextPage(false);
        return;
      }
      setResults((prev) => [...prev, ...(dataSet ?? [])]);
    } catch (error1) {
      setError(JSON.stringify(error1));
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  };

  const intObserver = useRef<IntersectionObserver>();
  const lastItemRef = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (item: any) => {
      if (isLoading) return;
      if (intObserver.current) intObserver.current.disconnect();
      intObserver.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          process(results);
        }
      });
      if (item) intObserver.current.observe(item);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoading, hasNextPage, results],
  );

  useEffect(() => {
    if (data.length) {
      setResults(data);
      setHasNextPage(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (error) {
    logError(error as any);
  }

  return { isLoading, isError: error, error, results, hasNextPage, lastItemRef };
};

export default useServerSideScroll;
